// AppsLauncher.js — the Apps tab: the tray of every unpinned item (built-in
// apps like Summary and Coding, and connected vendor apps), with the open
// one's content underneath. Right-click a tile to pin it or move it; Edit
// layout (from any right-click menu or the Edit button) makes the tabs and
// tiles jiggle so they can be dragged — in here to unpin, up onto the tabs to
// pin. Pinned items get their own tab and rail button; the order here is the
// order there.
import { useContext, useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';
import useVendorStore from '../../utils/vendorApps/useVendorStore';
import { LOCKED } from '../../utils/workspace/workspaceLayout';
import AppRequest from './AppRequest';
import ItemGlyph from './ItemGlyph';
import { railSettingAvailable, readRailSetting, subscribeRailSetting, writeRailSetting } from '../../utils/workspace/railSetting';
import { EditBadge, WorkspaceMenu, beginDrag, dragState, ensureJiggleStyle } from './WorkspaceEdit';

/** No connected apps yet: say they're coming, and ask which ones to build first. */
function AppsComingSoon() {
  return (
    <div className="d-flex align-items-start mx-1 my-2 p-3 rounded" style={{ gap: 12, background: '#f6f7fb', border: '1px solid #e9ecef' }} data-apps-coming-soon>
      <span style={{ width: 40, height: 40, flex: 'none', borderRadius: 11, background: '#5e72e4', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ItemGlyph item={{ kind: 'builtin', id: 'apps' }} size={22} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <AppRequest
          intro={
            <div className="d-flex flex-column" style={{ gap: 4 }}>
              <strong style={{ fontSize: 14, color: '#32325d' }}>Connected apps are coming soon</strong>
              <span className="small text-muted" style={{ lineHeight: 1.45 }}>
                Comprehend is adding the tools your clinic already uses. Tell us which ones you want first.
              </span>
            </div>
          }
        />
      </div>
    </div>
  );
}

/** The last tile, for everyone: asks which apps to connect next. */
function RequestTile({ selected, onClick }) {
  return (
    <button
      type="button"
      data-apps-request-tile
      onClick={onClick}
      title="Tell us which apps you want connected"
      aria-pressed={selected}
      className="vendor-app-tile"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 72, padding: '6px 4px', border: 0, background: 'transparent', cursor: 'pointer', opacity: selected ? 1 : 0.72, transition: 'opacity .1s' }}
    >
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          border: '1.5px dashed #adb5bd',
          color: '#8898aa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          lineHeight: 1,
          boxShadow: selected ? '0 0 0 2px #fff, 0 0 0 4px #5e72e4' : undefined,
        }}
      >
        +
      </span>
      <span style={{ fontSize: 11, lineHeight: 1.15, fontWeight: selected ? 700 : 500, color: selected ? '#5e72e4' : '#525f7f', whiteSpace: 'nowrap' }}>Request</span>
    </button>
  );
}

/** Edit mode's ribbon switch: whether the rail shows on EMR pages. Extension only. */
function RibbonToggle() {
  const [on, setOn] = useState(null);
  useEffect(() => {
    readRailSetting(setOn);
    return subscribeRailSetting(setOn);
  }, []);
  if (!railSettingAvailable() || on === null) return null;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      data-ribbon-toggle
      onClick={() => {
        setOn(!on);
        writeRailSetting(!on);
      }}
      title={on ? 'Hide the ribbon on EMR pages' : 'Show the ribbon on EMR pages'}
      className="d-flex align-items-center px-2 py-1 mb-1 rounded"
      style={{ gap: 8, width: '100%', border: '1px solid #e9ecef', background: '#fff', cursor: 'pointer', textAlign: 'left' }}
    >
      <small style={{ flex: 1, color: '#525f7f', lineHeight: 1.35 }}>Show the ribbon on EMR pages</small>
      <span aria-hidden="true" style={{ position: 'relative', flex: 'none', width: 30, height: 17, borderRadius: 9, background: on ? '#2dce89' : '#d0d5dd', transition: 'background .15s' }}>
        <span style={{ position: 'absolute', top: 2, left: on ? 15 : 2, width: 13, height: 13, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.2)', transition: 'left .15s' }} />
      </span>
    </button>
  );
}

function Tile({ item, index, selected, attention, workspace, onPick }) {
  const brand = item.color || '#5e72e4';
  const editing = workspace.editing;
  const dragged = workspace.dragging === item.id;
  return (
    <button
      type="button"
      draggable={editing}
      data-workspace-tile={item.id}
      onClick={() => {
        if (editing) return;
        workspace.select(item.id);
        onPick?.();
      }}
      onContextMenu={(e) => workspace.showMenu(e, item.id)}
      onDragStart={(e) => (editing ? beginDrag(e, item.id, workspace) : e.preventDefault())}
      onDragEnd={() => workspace.endDrag()}
      title={editing ? `Drag ${item.label} onto the tabs to pin it` : item.description || item.label}
      aria-pressed={selected}
      className={`vendor-app-tile${editing && !dragged ? ` cpt-jiggle${index % 2 ? ' cpt-jiggle-b' : ''}` : ''}`}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        width: 72,
        padding: '6px 4px',
        border: 0,
        background: 'transparent',
        cursor: editing ? 'grab' : 'pointer',
        // A ghost is where a dragged tile came from, until the drop.
        opacity: workspace.ghost === item.id ? 0.3 : dragged ? 0.55 : selected || editing ? 1 : 0.72,
        transition: 'opacity .1s',
      }}
    >
      <span
        style={{
          position: 'relative',
          width: 44,
          height: 44,
          borderRadius: 12,
          background: item.kind === 'vendor' && item.icon ? '#fff' : brand,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: selected && !editing ? `0 0 0 2px #fff, 0 0 0 4px ${brand}` : '0 1px 2px rgba(0,0,0,.12)',
          transition: 'box-shadow .12s ease-out',
        }}
      >
        {editing && <EditBadge kind="add" label={`Pin ${item.label}`} onClick={() => workspace.setPinned(item.id, true)} />}
        {item.kind === 'vendor' ? <ItemGlyph item={item} size={44} style={{ borderRadius: 12, fontSize: 18 }} /> : <ItemGlyph item={item} size={24} />}
        {attention && !editing && (
          <span
            aria-label={`Not linked to ${workspace.patientName || 'this patient'} yet`}
            title={`Not linked to ${workspace.patientName || 'this patient'} yet: open ${workspace.patientName || 'them'} in ${item.label} to link.`}
            style={{ position: 'absolute', top: -4, right: -4, width: 12, height: 12, borderRadius: '50%', background: brand, border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,.08)', zIndex: 1 }}
          />
        )}
      </span>
      <span style={{ fontSize: 11, lineHeight: 1.15, fontWeight: selected ? 700 : 500, color: selected ? brand : '#525f7f', maxWidth: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.label}
      </span>
    </button>
  );
}

export default function AppsLauncher({ workspace, active, renderBody }) {
  const { apiClient } = useContext(GlobalContext);
  const store = useVendorStore();
  const items = workspace.unpinned;
  const noConnectedApps = !workspace.items.some((i) => i.kind === 'vendor');
  // A tab being dragged down into the tray.
  const incoming = workspace.editing && workspace.preview?.pinned === false && workspace.isPinned(workspace.preview.id);
  const editing = workspace.editing;
  const selected = workspace.byId[workspace.selection] || null;
  const needing = new Set(store.appsNeedingAttention().map((a) => `app:${a.id}`));
  const trayIdle = !editing && noConnectedApps && items.length === 0;
  // "Request an app" takes the content area until another tile is picked.
  const [requesting, setRequesting] = useState(false);

  // Make sure the group's app list is loaded whenever this tab is shown, so an
  // admin's change (or a slow profile) never leaves it saying "no apps".
  useEffect(() => {
    if (active) store.ensureApps(apiClient);
  }, [active, apiClient, store]);

  // Esc, or leaving the Apps tab, ends edit mode.
  useEffect(() => {
    if (!editing) return undefined;
    ensureJiggleStyle();
    const onKey = (e) => e.key === 'Escape' && workspace.setEditing(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, workspace]);
  useEffect(() => {
    if (!active && editing) workspace.setEditing(false);
  }, [active, editing, workspace]);

  return (
    <div className="d-flex flex-column" data-apps-launcher>
      {editing && (
        <div className="d-flex align-items-center px-2 py-1 mb-1 rounded" style={{ background: '#f1f3f9', gap: 8 }}>
          <small className="text-muted" style={{ flex: 1, lineHeight: 1.35 }}>
            Drag to rearrange: tabs down here to unpin, tiles up to pin. Your first tab is where each visit opens.
          </small>
          <Button size="sm" color="link" className="py-0 text-muted" onClick={workspace.reset}>
            Reset
          </Button>
          <Button size="sm" color="primary" className="py-0" onClick={() => workspace.setEditing(false)}>
            Done
          </Button>
        </div>
      )}
      {editing && <RibbonToggle />}
      {/* Nothing to show in the tray and no apps yet: the coming-soon card
          stands alone, and rearranging moves under it. */}
      {!trayIdle && (
      <div
        className="d-flex align-items-start flex-wrap px-1 pt-1 rounded"
        data-apps-tray
        style={{ gap: 2, minHeight: 64, boxShadow: incoming ? 'inset 0 0 0 2px #5e72e4' : undefined, transition: 'box-shadow .1s' }}
        // The tray is one drop zone and re-flows live, like the tab bar: the
        // tile goes before or after the one under the pointer (the side it is
        // moving toward); empty space means the end.
        onDragOver={(e) => {
          const from = dragState.get();
          if (!editing || !from || LOCKED.includes(from)) return;
          e.preventDefault();
          const tile = e.target?.closest?.('[data-workspace-tile]');
          const overId = tile?.dataset.workspaceTile;
          if (!overId) {
            if (!workspace.preview || workspace.preview.pinned !== false) workspace.setPreview({ id: from, pinned: false });
            return;
          }
          if (overId === from) return;
          const list = items.map((i) => i.id);
          const fromAt = list.indexOf(from);
          let after;
          if (fromAt !== -1 && workspace.ghost !== from) after = fromAt < list.indexOf(overId);
          else {
            const rect = tile.getBoundingClientRect();
            after = e.clientX > rect.left + rect.width / 2;
          }
          workspace.setPreview({ id: from, pinned: false, targetId: overId, after });
        }}
        onDrop={(e) => {
          if (!dragState.get()) return;
          e.preventDefault();
          workspace.commitPreview();
          // The drag ends here, not on dragend: a tab dropped into the tray has
          // left the tab bar, so its dragend never arrives.
          workspace.endDrag();
        }}
      >
        {items.map((item, index) => (
          <Tile key={item.id} item={item} index={index} selected={!editing && !requesting && selected?.id === item.id} attention={needing.has(item.id)} workspace={workspace} onPick={() => setRequesting(false)} />
        ))}
        {!editing && <RequestTile selected={requesting} onClick={() => setRequesting(true)} />}
        {items.length === 0 && !(noConnectedApps && !editing) && (
          <p className="text-muted small p-2 mb-0" style={{ flex: 1 }}>
            {editing ? 'Drag a tab here to move it into Apps.' : 'Everything is pinned to its own tab. Right-click a tab to move it back in here.'}
          </p>
        )}
        {!editing && (
          <Button size="sm" color="link" className="ml-auto py-1 text-muted" onClick={() => workspace.setEditing(true)} title="Rearrange tabs and apps (or right-click any of them)">
            Edit
          </Button>
        )}
      </div>
      )}
      {/* With tiles in the tray, the coming-soon card is the Request tile's
          content, like the request form is once apps exist, so it never sits
          between a tile and the app it opened. */}
      {!editing && noConnectedApps && (trayIdle || requesting) && <AppsComingSoon />}
      {trayIdle && (
        <div className="d-flex justify-content-end px-1">
          <Button size="sm" color="link" className="py-0 text-muted" onClick={() => workspace.setEditing(true)} title="Rearrange tabs and apps (or right-click any of them)">
            Rearrange tabs
          </Button>
        </div>
      )}
      {!editing && requesting && !noConnectedApps && (
        <div className="mx-1 my-2 p-3 rounded" style={{ background: '#f6f7fb', border: '1px solid #e9ecef' }}>
          <AppRequest
            intro={
              <div className="d-flex flex-column" style={{ gap: 4 }}>
                <strong style={{ fontSize: 14, color: '#32325d' }}>Which apps should we connect next?</strong>
                <span className="small text-muted" style={{ lineHeight: 1.45 }}>Tap the kinds you use, and name them if you can.</span>
              </div>
            }
          />
        </div>
      )}
      {!editing && !requesting && selected && <div className="pt-1">{renderBody(selected)}</div>}
      <WorkspaceMenu workspace={workspace} />
    </div>
  );
}
